import FormattedMessage from 'I18n/components/FormattedMessage';
import { Flex } from 'foundations-components/components/Flex';
import { Text } from 'foundations-components/components/Text';
import { View } from 'foundations-components/components/View';
import { getButtonVariantProps, getTrellisButtonVariantProps } from 'pricing-pages-lib/utils/buttonVariantUtils';
import { PriceDisplayWithTerm } from 'pricing-pages-ui-components/price/PriceDisplayWithTerm';
import { MERCH_CARD } from 'ui-addon-upgrades/_core/common/constants/PurchaseMotionLocations';
import { getProductLevelFromApiNameString } from 'ui-addon-upgrades/_core/features/FeaturesUtils';
import { SMART_CRM_FEATURE_TABLE_HEADER } from '../../../constants/tracking/TrackingSources';
import SingleProductCTA from '../../../features/purchaseMotions/components/SingleProductCTA';
import { TalkToSalesFallback } from '../../../features/purchaseMotions/components/TalkToSalesFallback';
import { useBundledProductPurchaseMotions } from '../../../features/purchaseMotions/hooks/useBundledProductPurchaseMotions';
import { useGlobalLoading } from '../../../hooks/loadingHooks';
import { usePurchaseableProductsForTerm } from '../../../hooks/useBulkPurchaseOptions';
import { useCurrencyCodeUnitToDisplay, useDisplayedCurrencyCode } from '../../../hooks/useCurrencies';
import { useAppSelector } from '../../../reducers';
import { getIsLatestVersionOfProductInProductInventorySelector } from '../../../selectors/ProductInventorySelectors';
import { productSelector } from '../../../selectors/ProductsSelectors';
import { useQueryParamTerm } from '../../../utils/urls/appQueryParamUtils';
import SkeletonLoadWrapper from '../../common/loading/SkeletonLoadWrapper';
import ErrorBoundary from '../../error-boundary/ErrorBoundary';
import FeatureTableColTag from '../FeatureTableColTag';
import { jsx as _jsx } from "react/jsx-runtime";
import { jsxs as _jsxs } from "react/jsx-runtime";
const SmartCrmFeatureTableHeaderCell = ({
  apiName,
  screen
}) => {
  const isAppReady = useGlobalLoading();
  const product = useAppSelector(state => {
    if (!isAppReady) return null;
    return productSelector(state, {
      apiName
    });
  });
  const skuIdToUse = product === null || product === void 0 ? void 0 : product.currentSkuId;
  const isCurrentPlanLevel = useAppSelector(state => isAppReady && apiName ? getIsLatestVersionOfProductInProductInventorySelector(state, {
    productIdentifier: {
      apiName
    }
  }) : false);
  const purchaseMotions = useBundledProductPurchaseMotions({
    skuId: skuIdToUse
  });
  const {
    queryParamTerm
  } = useQueryParamTerm();
  const {
    purchaseOptionsForTerm,
    loading: purchaseOptionsForTermLoading
  } = usePurchaseableProductsForTerm({
    queryParamTerm,
    apiName: product === null || product === void 0 ? void 0 : product.apiName
  });
  const loading = purchaseOptionsForTermLoading || !isAppReady;
  const currencyCode = useDisplayedCurrencyCode();
  const currencyCodeUnit = useCurrencyCodeUnitToDisplay();
  const productLevel = (product === null || product === void 0 ? void 0 : product.productLevel) || getProductLevelFromApiNameString(apiName);
  if (!productLevel || !purchaseOptionsForTerm && !purchaseOptionsForTermLoading) {
    return null;
  }
  const ctaVariantProps = getButtonVariantProps({
    variant: 'table-header'
  });
  const trellisCtaVariantProps = getTrellisButtonVariantProps({
    variant: 'table-header',
    visible: isCurrentPlanLevel // render space as if button is visible to prevent layout shift
  });
  return /*#__PURE__*/_jsxs(Flex, {
    paddingInline: "0",
    flexDirection: "column",
    alignItems: "center",
    children: [/*#__PURE__*/_jsx(FeatureTableColTag, {
      apiName: apiName
    }), /*#__PURE__*/_jsx(Text, {
      as: "strong",
      typography: "heading-50",
      children: /*#__PURE__*/_jsx(FormattedMessage, {
        message: `productTile.${apiName}.title`
      })
    }), /*#__PURE__*/_jsx(View, {
      marginBlock: "100",
      children: /*#__PURE__*/_jsx(SkeletonLoadWrapper, {
        isLoading: loading,
        heights: [20],
        widths: [140],
        children: purchaseOptionsForTerm && /*#__PURE__*/_jsx(PriceDisplayWithTerm, {
          pricingInfo: purchaseOptionsForTerm,
          seleniumId: apiName || 'loading',
          currencyCode: currencyCode,
          currencyCodeUnit: currencyCodeUnit
        })
      })
    }), /*#__PURE__*/_jsx(SkeletonLoadWrapper, {
      isLoading: !isAppReady,
      heights: [40],
      widths: [140],
      children: /*#__PURE__*/_jsx(View, Object.assign({}, trellisCtaVariantProps, {
        children: /*#__PURE__*/_jsx(ErrorBoundary, {
          fallback: /*#__PURE__*/_jsx(TalkToSalesFallback, {
            apiName: apiName,
            trackingSource: SMART_CRM_FEATURE_TABLE_HEADER,
            screen: screen
          }),
          children: /*#__PURE__*/_jsx(SingleProductCTA, Object.assign({
            purchaseMotion: purchaseMotions[0],
            apiName: apiName,
            screen: screen,
            trackingSource: SMART_CRM_FEATURE_TABLE_HEADER,
            purchaseMotionLocation: MERCH_CARD,
            skipInterstitialPage: true
          }, ctaVariantProps), purchaseMotions[0])
        })
      }))
    })]
  });
};
export default SmartCrmFeatureTableHeaderCell;